/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Transaction Detail Audit Record Modal (Task 10.6.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  X,
  Copy,
  Check,
  ShieldCheck,
  Calendar,
  Layers,
  Cpu,
  Hash,
  Globe,
  Key,
  DollarSign,
  ArrowRight,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useCreditLedgerStore } from '../../stores/useCreditLedgerStore';

export const TransactionDetailModal: React.FC = () => {
  const { t } = useTranslation();
  const { isDetailModalOpen, selectedEntry, setDetailModalOpen } = useCreditLedgerStore();
  const [copiedId, setCopiedId] = useState(false);

  if (!isDetailModalOpen || !selectedEntry) return null;

  const handleCopyTxId = async () => {
    try {
      await navigator.clipboard.writeText(selectedEntry.id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    } catch {
      // Fallback
    }
  };

  const isCredit = selectedEntry.amount > 0;
  const isDebit = selectedEntry.amount < 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="relative w-full max-w-xl rounded-2xl border border-surface-border bg-surface p-6 shadow-2xl space-y-5 overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-start justify-between border-b border-surface-border pb-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="size-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <ShieldCheck className="size-4.5" />
              </div>
              <h2 className="text-base font-bold font-heading text-foreground">
                {t('ledger.detailTitle')}
              </h2>
            </div>
            <p className="text-xs text-foreground-muted">
              {t('ledger.detailSubtitle')}
            </p>
          </div>

          <button
            onClick={() => setDetailModalOpen(false)}
            className="p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-elevated transition-colors"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Transaction ID & Verification Status Bar */}
        <div className="flex items-center justify-between p-3 rounded-xl bg-surface-subtle border border-surface-border text-xs">
          <div className="flex items-center gap-2">
            <Hash className="size-3.5 text-primary" />
            <span className="font-mono font-bold text-foreground">
              {selectedEntry.id}
            </span>
            <button
              onClick={handleCopyTxId}
              className="p-1 rounded text-foreground-muted hover:text-primary transition-colors"
              title="Copy ID"
            >
              {copiedId ? (
                <Check className="size-3.5 text-emerald-500" />
              ) : (
                <Copy className="size-3.5" />
              )}
            </button>
          </div>

          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-bold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
            <ShieldCheck className="size-3" />
            {t('ledger.detailVerified')}
          </span>
        </div>

        {/* Double-Entry Balance Audit Trail */}
        <div className="p-4 rounded-xl border border-surface-border bg-surface-elevated space-y-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-foreground-muted">
            Double-Entry Financial Audit Trail
          </span>
          <div className="flex items-center justify-between text-xs">
            <div className="space-y-0.5">
              <span className="text-[10px] text-foreground-muted">{t('ledger.detailBalanceBefore')}</span>
              <p className="font-mono font-bold text-foreground">
                {selectedEntry.balanceBefore.toLocaleString()} CR
              </p>
            </div>

            <div className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-surface border border-surface-border">
              <span
                className={`font-mono font-extrabold text-sm ${
                  isCredit ? 'text-emerald-500' : isDebit ? 'text-rose-500' : 'text-foreground'
                }`}
              >
                {isCredit ? `+${selectedEntry.amount}` : selectedEntry.amount} CR
              </span>
              <ArrowRight className="size-3.5 text-foreground-muted" />
            </div>

            <div className="space-y-0.5 text-right">
              <span className="text-[10px] text-foreground-muted">{t('ledger.detailBalanceAfter')}</span>
              <p className="font-mono font-bold text-primary text-sm">
                {selectedEntry.balanceAfter.toLocaleString()} CR
              </p>
            </div>
          </div>
        </div>

        {/* Two-Column Metadata Matrix */}
        <div className="grid grid-cols-2 gap-3 text-xs">
          {/* Timestamp */}
          <div className="p-3 rounded-xl border border-surface-border bg-surface-subtle space-y-1">
            <span className="text-[10px] text-foreground-muted flex items-center gap-1">
              <Calendar className="size-3" />
              {t('ledger.detailTimestamp')}
            </span>
            <p className="font-mono text-foreground font-semibold">
              {new Date(selectedEntry.timestamp).toLocaleString()}
            </p>
          </div>

          {/* Reason & Type */}
          <div className="p-3 rounded-xl border border-surface-border bg-surface-subtle space-y-1">
            <span className="text-[10px] text-foreground-muted flex items-center gap-1">
              <Layers className="size-3" />
              {t('ledger.detailType')}
            </span>
            <p className="font-semibold text-primary">
              {selectedEntry.type}
            </p>
          </div>

          {/* AI Model */}
          <div className="p-3 rounded-xl border border-surface-border bg-surface-subtle space-y-1">
            <span className="text-[10px] text-foreground-muted flex items-center gap-1">
              <Cpu className="size-3" />
              {t('ledger.detailModel')}
            </span>
            <p className="font-mono text-foreground font-semibold">
              {selectedEntry.modelCode || 'System Core'}
            </p>
          </div>

          {/* Units / Consumption */}
          <div className="p-3 rounded-xl border border-surface-border bg-surface-subtle space-y-1">
            <span className="text-[10px] text-foreground-muted flex items-center gap-1">
              <DollarSign className="size-3" />
              {t('ledger.detailUnits')}
            </span>
            <p className="font-semibold text-foreground">
              {selectedEntry.unitsUsed || '—'}
            </p>
          </div>

          {/* Reference Job ID */}
          <div className="p-3 rounded-xl border border-surface-border bg-surface-subtle space-y-1">
            <span className="text-[10px] text-foreground-muted flex items-center gap-1">
              <Hash className="size-3" />
              {t('ledger.detailReference')}
            </span>
            <p className="font-mono text-[11px] text-foreground-muted">
              {selectedEntry.referenceId || 'N/A'}
            </p>
          </div>

          {/* Edge Region & IP */}
          <div className="p-3 rounded-xl border border-surface-border bg-surface-subtle space-y-1">
            <span className="text-[10px] text-foreground-muted flex items-center gap-1">
              <Globe className="size-3" />
              {t('ledger.detailClientIp')}
            </span>
            <p className="font-mono text-[11px] text-foreground-muted">
              {selectedEntry.region} ({selectedEntry.clientIp})
            </p>
          </div>
        </div>

        {/* Idempotency Key */}
        {selectedEntry.idempotencyKey && (
          <div className="p-2.5 rounded-xl border border-surface-border bg-surface text-[11px] flex items-center justify-between text-foreground-muted">
            <div className="flex items-center gap-1.5 font-mono">
              <Key className="size-3 text-primary" />
              <span>Idempotency: {selectedEntry.idempotencyKey}</span>
            </div>
            <span className="text-[10px] text-emerald-500 font-semibold">
              Single-Execution Guard
            </span>
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-end pt-2 border-t border-surface-border">
          <button
            onClick={() => setDetailModalOpen(false)}
            className="px-5 py-2 rounded-xl bg-surface-elevated hover:bg-surface-subtle border border-surface-border text-xs font-semibold text-foreground transition-all"
          >
            {t('ledger.closeBtn')}
          </button>
        </div>
      </div>
    </div>
  );
};
